/**
 * Password Validation Utility
 *
 * Reusable password validation for registration, password reset, and other forms.
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: PasswordValidationError[];
  requirements: PasswordRequirement[];
}

export interface PasswordValidationError {
  code: PasswordErrorCode;
  message: string;
}

export interface PasswordRequirement {
  code: PasswordErrorCode;
  labelKey: string;
  met: boolean;
}

export type PasswordErrorCode =
  | 'MIN_LENGTH'
  | 'UPPERCASE'
  | 'LOWERCASE'
  | 'NUMBER'
  | 'SPECIAL_CHAR'
  | 'MISMATCH';

export const PASSWORD_MIN_LENGTH = 8;
export const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;

/**
 * Validates a password against all requirements
 */
export function validatePassword(password: string): PasswordValidationResult {
  const requirements: PasswordRequirement[] = [
    {
      code: 'MIN_LENGTH',
      labelKey: 'auth.passwordRequirementsList.minLength',
      met: password.length >= PASSWORD_MIN_LENGTH,
    },
    {
      code: 'UPPERCASE',
      labelKey: 'auth.passwordRequirementsList.uppercase',
      met: /[A-Z]/.test(password),
    },
    {
      code: 'LOWERCASE',
      labelKey: 'auth.passwordRequirementsList.lowercase',
      met: /[a-z]/.test(password),
    },
    {
      code: 'NUMBER',
      labelKey: 'auth.passwordRequirementsList.number',
      met: /[0-9]/.test(password),
    },
    {
      code: 'SPECIAL_CHAR',
      labelKey: 'auth.passwordRequirementsList.specialChar',
      met: SPECIAL_CHARS_REGEX.test(password),
    },
  ];

  const errors: PasswordValidationError[] = requirements
    .filter((req) => !req.met)
    .map((req) => ({
      code: req.code,
      message: req.labelKey,
    }));

  return {
    isValid: errors.length === 0,
    errors,
    requirements,
  };
}

/**
 * Validates password and confirmation match
 */
export function validatePasswordMatch(
  password: string,
  confirmPassword: string
): PasswordValidationError | null {
  if (password !== confirmPassword) {
    return {
      code: 'MISMATCH',
      message: 'Passwords do not match',
    };
  }
  return null;
}

/**
 * Full password validation including confirmation
 */
export function validatePasswordWithConfirmation(
  password: string,
  confirmPassword: string
): PasswordValidationResult {
  const result = validatePassword(password);

  const mismatchError = validatePasswordMatch(password, confirmPassword);
  if (mismatchError) {
    result.errors.push(mismatchError);
    result.isValid = false;
    result.requirements.push({
      code: 'MISMATCH',
      labelKey: 'auth.passwordRequirementsList.match',
      met: false,
    });
  } else if (confirmPassword.length > 0) {
    result.requirements.push({
      code: 'MISMATCH',
      labelKey: 'auth.passwordRequirementsList.match',
      met: true,
    });
  }

  return result;
}

/**
 * Calculate password strength (0-100)
 */
export function calculatePasswordStrength(password: string): number {
  if (!password) return 0;

  let strength = 0;
  const { requirements } = validatePassword(password);

  // Each requirement met adds 20 points (5 requirements = 100%)
  requirements.forEach((req) => {
    if (req.met) strength += 20;
  });

  return strength;
}

/**
 * Get strength level label
 */
export function getStrengthLevel(strength: number): 'weak' | 'fair' | 'good' | 'strong' {
  if (strength < 40) return 'weak';
  if (strength < 60) return 'fair';
  if (strength < 80) return 'good';
  return 'strong';
}
